import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { CORE_SPORTS } from '@utils/sports';
import {
  SPONSOR_BUDGET_BANDS,
  SPONSOR_GEOGRAPHY_OPTIONS,
  SPONSOR_INTEREST_CATEGORIES,
  SPONSORSHIP_TIERS,
  type SponsorshipOpportunity,
} from '@utils/sponsor';
import { useAuthStore } from '@store/authStore';
import { UserRole } from '@types/index';

interface ExpressInterestFormProps {
  opportunity?: SponsorshipOpportunity | null;
  compact?: boolean;
  onSuccess?: () => void;
}

const ExpressInterestForm: React.FC<ExpressInterestFormProps> = ({
  opportunity,
  compact = false,
  onSuccess,
}) => {
  const { user, isAuthenticated } = useAuthStore();
  const isSponsorAccount = isAuthenticated && (user?.role === UserRole.SPONSOR || user?.role === 'sponsor');
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [country, setCountry] = useState('India');
  const [preferredTier, setPreferredTier] = useState(opportunity?.sponsorshipTier || 'flexible');
  const [budgetBand, setBudgetBand] = useState('flexible');
  const [sports, setSports] = useState<string[]>(opportunity?.sport ? [opportunity.sport] : ['Badminton']);
  const [interestCategories, setInterestCategories] = useState<string[]>(
    opportunity?.opportunityType ? [opportunity.opportunityType] : ['contest']
  );
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const endpoint = isSponsorAccount ? '/api/sponsor/applications' : '/api/sponsor-applications';
      const body = isSponsorAccount
        ? { message, opportunityId: opportunity?.id || null }
        : {
            companyName,
            contactName,
            contactEmail,
            contactPhone,
            websiteUrl,
            country,
            preferredTier,
            budgetBand,
            sports,
            interestCategories,
            message,
            opportunityId: opportunity?.id || null,
          };
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to submit interest');
      }
      setSuccess(data.message || 'Interest submitted successfully.');
      setCompanyName('');
      setContactName('');
      setContactEmail('');
      setContactPhone('');
      setWebsiteUrl('');
      setMessage('');
      onSuccess?.();
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      id="express-interest"
      sx={{
        p: compact ? 2 : { xs: 2.5, md: 3 },
        borderRadius: 3,
        background: 'linear-gradient(145deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
        border: '1px solid rgba(148, 163, 184, 0.35)',
        boxShadow: '0 24px 48px rgba(15, 23, 42, 0.12)',
      }}
    >
      <Stack spacing={0.5} sx={{ mb: 2.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: '#0f172a' }}>
          Express your interest
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {opportunity
            ? `Applying for: ${opportunity.title}`
            : 'Tell us about your brand. Tier and package are matched to your budget and competitive landscape — no public pricing.'}
        </Typography>
        {opportunity && (
          <Chip
            size="small"
            label={`Suggested tier: ${SPONSORSHIP_TIERS.find((t) => t.id === opportunity.sponsorshipTier)?.label || opportunity.sponsorshipTier}`}
            sx={{ alignSelf: 'flex-start', fontWeight: 800, bgcolor: '#fef3c7', color: '#92400e' }}
          />
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField fullWidth required label="Company / Brand name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth required label="Contact name" value={contactName} onChange={(e) => setContactName(e.target.value)} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth required type="email" label="Work email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Phone (optional)" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Website" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="https://" />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Country / primary market</InputLabel>
            <Select label="Country / primary market" value={country} onChange={(e) => setCountry(String(e.target.value))}>
              {SPONSOR_GEOGRAPHY_OPTIONS.map((g) => (
                <MenuItem key={g} value={g}>{g}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Preferred tier</InputLabel>
            <Select label="Preferred tier" value={preferredTier} onChange={(e) => setPreferredTier(String(e.target.value))}>
              {SPONSORSHIP_TIERS.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Budget band</InputLabel>
            <Select label="Budget band" value={budgetBand} onChange={(e) => setBudgetBand(String(e.target.value))}>
              {SPONSOR_BUDGET_BANDS.map((b) => (
                <MenuItem key={b.id} value={b.id}>{b.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Sports of interest</InputLabel>
            <Select
              multiple
              label="Sports of interest"
              value={sports}
              onChange={(e) => setSports(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              renderValue={(selected) => (selected as string[]).join(', ')}
            >
              {CORE_SPORTS.map((sport) => (
                <MenuItem key={sport} value={sport}>{sport}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Partnership interests</InputLabel>
            <Select
              multiple
              label="Partnership interests"
              value={interestCategories}
              onChange={(e) => setInterestCategories(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              renderValue={(selected) =>
                (selected as string[])
                  .map((id) => SPONSOR_INTEREST_CATEGORIES.find((c) => c.id === id)?.label || id)
                  .join(', ')
              }
            >
              {SPONSOR_INTEREST_CATEGORIES.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>{cat.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Message"
            placeholder="Tell us about your campaign goals, target audience, and timing..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            endIcon={<SendIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 900,
              px: 4,
              py: 1.25,
              background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 55%, #7c3aed 100%)',
              boxShadow: '0 12px 28px rgba(99, 102, 241, 0.35)',
            }}
          >
            {loading ? 'Submitting…' : 'Submit interest'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExpressInterestForm;
